import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Autocomplete,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  Description,
  CloudUpload,
  Storage,
  Delete,
  CheckCircle,
  RadioButtonUnchecked,
  Folder,
} from '@mui/icons-material';
import SharePointFolderPicker from '../Document/SharePointFolderPicker';
import { apiService } from '../../services/api';
import { DocumentAssociation, ActionType, PlanningTask, PlanningPhase, ProjectPlan } from '../../types';

interface DocumentAssociationPanelProps {
  actionId: number;
  actionType: ActionType;
  requiredDocumentCount?: number | null;
  projectId: number;
  projectPlan?: ProjectPlan; // Optional: for finding prerequisite CREATE actions
  onCreateAssociations?: (associations: DocumentAssociation[]) => void;
  onUpdateAssociations?: (associations: DocumentAssociation[]) => void;
}

interface AvailableCreateDocument {
  actionId: number; // Source CREATE action ID
  actionDocumentId: number; // Specific document ID from the source CREATE action
  documentNumber: number;
  documentTitle?: string | null; // NEW - Document title
  documentDescription?: string | null; // NEW - Document description
  fileName?: string;
  storageType?: 'SHAREPOINT' | 'LOCAL';
  label: string;
}

export default function DocumentAssociationPanel({
  actionId,
  actionType,
  requiredDocumentCount: initialRequiredCount,
  projectId,
  projectPlan,
  onCreateAssociations,
  onUpdateAssociations,
}: DocumentAssociationPanelProps) {
  const [associations, setAssociations] = useState<DocumentAssociation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [requiredDocumentCount, setRequiredDocumentCount] = useState<number | null>(
    initialRequiredCount || null
  );
  const [_selectedDocuments, setSelectedDocuments] = useState<number[]>([]); // For REVISE: array of documentNumbers (for display)
  const [selectedDocumentActionIds, setSelectedDocumentActionIds] = useState<number[]>([]); // For REVISE: array of actionDocumentIds (for tracking)
  const [_selectedDocument, setSelectedDocument] = useState<number | null>(null); // For REVIEW/APPROVE: single documentNumber (for display)
  const [selectedDocumentActionId, setSelectedDocumentActionId] = useState<number | null>(null); // For REVIEW/APPROVE: single actionDocumentId (for tracking)
  const [availableCreateDocuments, setAvailableCreateDocuments] = useState<AvailableCreateDocument[]>([]);

  // Load existing associations
  useEffect(() => {
    const loadAssociations = async () => {
      setLoading(true);
      setError(null);
      try {
        // Always load action details to get requiredDocumentCount
        const action = await apiService.getAction(actionId);
        const actionRequiredCount = action.requiredDocumentCount;
        if (actionRequiredCount !== undefined) {
          setRequiredDocumentCount(actionRequiredCount);
        }
        
        // Load document associations
        const docs = await apiService.getActionDocuments(actionId);
        const placeholderCount = docs.filter(d => d.isPlaceholder === true || (!d.spItemId && !d.localFileId)).length;
        
        console.log('Loaded associations:', {
          actionId,
          requiredDocumentCount: actionRequiredCount,
          associationsCount: docs.length,
          placeholderCount: placeholderCount,
          associations: docs.map(doc => ({
            actionDocumentId: doc.actionDocumentId,
            documentNumber: doc.documentNumber,
            documentTitle: doc.documentTitle,
            documentDescription: doc.documentDescription,
            documentType: doc.documentType,
            sourceActionId: doc.sourceActionId,
            sourceActionDocumentId: doc.sourceActionDocumentId,
            isPlaceholder: doc.isPlaceholder,
            isRequired: doc.isRequired,
            hasFile: !!(doc.spItemId || doc.localFileId)
          }))
        });
        
        // If we have a requiredDocumentCount but no placeholders exist, automatically trigger their creation
        // This handles the case where actions are created from templates with requiredDocumentCount already set
        // or when requiredDocumentCount is set for the first time
        if (actionType === 'CREATE' && actionRequiredCount && actionRequiredCount > 0 && placeholderCount === 0) {
          console.log('No placeholders found but requiredDocumentCount is set. Automatically creating placeholders...');
          try {
            // Trigger placeholder creation by updating the requiredDocumentCount (even to the same value)
            // This will cause the backend to create placeholders if they don't exist
            await apiService.updateAction(actionId, {
              requiredDocumentCount: actionRequiredCount
            });
            
            // Wait a brief moment for backend to fully process
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Reload associations after placeholder creation with retry logic
            let updatedDocs: DocumentAssociation[] = [];
            let retryCount = 0;
            const maxRetries = 3;
            
            while (retryCount < maxRetries && updatedDocs.length === 0) {
              console.log(`Reloading associations after auto-create (attempt ${retryCount + 1}/${maxRetries})...`);
              updatedDocs = await apiService.getActionDocuments(actionId);
              
              if (updatedDocs.length === 0 && retryCount < maxRetries - 1) {
                console.log(`No documents found, waiting 1 second before retry...`);
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
              retryCount++;
            }
            
            console.log('Placeholders auto-created. Raw response from getActionDocuments:', updatedDocs);
            console.log('Placeholders auto-created. Reloaded associations:', {
              associationsCount: updatedDocs.length,
              placeholderCount: updatedDocs.filter(d => d.isPlaceholder === true || (!d.spItemId && !d.localFileId)).length,
              allDocs: updatedDocs.map(doc => ({
                actionDocumentId: doc.actionDocumentId,
                documentNumber: doc.documentNumber,
                isPlaceholder: doc.isPlaceholder,
                isRequired: doc.isRequired,
                spItemId: doc.spItemId,
                localFileId: doc.localFileId,
                documentTitle: doc.documentTitle,
                documentDescription: doc.documentDescription
              }))
            });
            setAssociations(updatedDocs);
          } catch (createErr: unknown) {
            console.warn('Failed to auto-create placeholders:', createErr);
            // Don't set error - we'll show the alert in the UI instead
            setAssociations(docs); // Use the original (empty) associations
          }
        } else {
          setAssociations(docs);
        }
      } catch (err: unknown) {
        console.error('Failed to load document associations:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load document associations';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadAssociations();
  }, [actionId, actionType]);

  // Initialize selected state from existing associations when both associations and available documents are loaded
  useEffect(() => {
    if (associations.length === 0 || availableCreateDocuments.length === 0) return;
    if (actionType !== 'REVISE' && actionType !== 'REVIEW' && actionType !== 'APPROVE' && actionType !== 'VALIDATE') return;

    const sourceAssociations = associations.filter(doc =>
      doc.documentType === 'SOURCE' || doc.documentType === 'REFERENCE' || !!doc.sourceActionId
    );
    
    if (sourceAssociations.length > 0) {
      if (actionType === 'REVISE' || actionType === 'REVIEW' || actionType === 'VALIDATE') {
        // Match associations to available documents by sourceActionDocumentId (preferred) or sourceActionId + documentNumber
        const matchedDocs = sourceAssociations
          .map(assoc => {
            // Try to find matching available document
            // First try by sourceActionDocumentId (most specific), then by sourceActionId + documentNumber
            const matched = availableCreateDocuments.find(avail => 
              assoc.sourceActionDocumentId === avail.actionDocumentId ||
              (assoc.documentNumber === avail.documentNumber && assoc.sourceActionId === avail.actionId)
            );
            return matched;
          })
          .filter((doc): doc is AvailableCreateDocument => doc !== undefined);

        if (matchedDocs.length > 0) {
          setSelectedDocuments(matchedDocs.map(d => d.documentNumber));
          setSelectedDocumentActionIds(matchedDocs.map(d => d.actionDocumentId));
          console.log(`Initialized ${actionType} selected documents:`, {
            selectedDocuments: matchedDocs.map(d => d.documentNumber),
            selectedActionIds: matchedDocs.map(d => d.actionDocumentId)
          });
        }
      } else {
        // REVIEW/APPROVE - single document
        const matched = availableCreateDocuments.find(avail => 
          sourceAssociations.some(assoc =>
            assoc.sourceActionDocumentId === avail.actionDocumentId ||
            (assoc.documentNumber === avail.documentNumber && assoc.sourceActionId === avail.actionId)
          )
        );

        if (matched) {
          setSelectedDocument(matched.documentNumber);
          setSelectedDocumentActionId(matched.actionDocumentId);
          console.log('Initialized REVIEW/APPROVE selected document:', {
            documentNumber: matched.documentNumber,
            actionDocumentId: matched.actionDocumentId
          });
        }
      }
    }
  }, [associations, availableCreateDocuments, actionType]);

  // Load available CREATE documents from prerequisite actions
  useEffect(() => {
    const loadAvailableDocuments = async () => {
      if (!projectPlan || (actionType !== 'REVISE' && actionType !== 'REVIEW' && actionType !== 'APPROVE')) {
        setAvailableCreateDocuments([]);
        return;
      }

      // Find current action's task
      let currentTask: PlanningTask | null = null;
      let currentPhase: PlanningPhase | null = null;

      projectPlan.phases.forEach(phase => {
        phase.tasks.forEach((task: PlanningTask) => {
          task.actions.forEach((action) => {
            if (action.actionId === actionId) {
              currentTask = task;
              currentPhase = phase;
            }
          });
        });
      });

      if (!currentTask || !currentPhase) {
        setAvailableCreateDocuments([]);
        return;
      }

      // TypeScript type narrowing - we know these are not null after the check above
      const task = currentTask as PlanningTask;
      const phase = currentPhase as PlanningPhase;

      // Find all CREATE actions from prerequisite tasks
      const prerequisiteTaskIds = task.prerequisites || [];
      const prerequisiteTasks = projectPlan.phases
        .flatMap(p => p.tasks)
        .filter((t: PlanningTask) => prerequisiteTaskIds.includes(t.taskId));

      // Also include CREATE actions from earlier tasks in the same phase
      const samePhaseTasks = phase.tasks.filter(
        (t: PlanningTask) => t.displayOrder < task.displayOrder
      );

      const allSourceTasks = [...prerequisiteTasks, ...samePhaseTasks];

      // Fetch documents for each CREATE action
      const fetchPromises = allSourceTasks.flatMap((sourceTask: PlanningTask) =>
        sourceTask.actions
          .filter((action) => action.actionType === 'CREATE')
          .map(async (action) => {
            try {
              // Fetch documents for this CREATE action
              const actionDocs = await apiService.getActionDocuments(action.actionId);
              
              // If action has multiple documents, show each one
              if (actionDocs.length > 0) {
                return actionDocs.map(doc => {
                  const docTitle = doc.documentTitle || `Document ${doc.documentNumber}`;
                  return {
                    actionId: action.actionId, // Source CREATE action ID
                    actionDocumentId: doc.actionDocumentId, // Specific document ID from source action
                    documentNumber: doc.documentNumber || 1,
                    documentTitle: doc.documentTitle || null,
                    documentDescription: doc.documentDescription || null,
                    fileName: doc.fileName || undefined,
                    storageType: doc.storageType || undefined,
                    label: `${docTitle} – ${action.actionType}`,
                  };
                });
              } else {
                // Fallback: no documents yet, show action (but can't create association without actionDocumentId)
                // Return empty array - user needs to wait for documents to be created
                return [];
              }
            } catch (err) {
              // If fetch fails, can't create association without actionDocumentId
              console.warn(`Failed to fetch documents for action ${action.actionId}:`, err);
              return []; // Return empty - need documents to be available
            }
          })
      );

      const results = await Promise.all(fetchPromises);
      const allDocuments = results.flat();
      setAvailableCreateDocuments(allDocuments);
    };

    loadAvailableDocuments();
  }, [projectPlan, actionId, actionType]);

  // Handle RequiredDocumentCount change for CREATE actions
  const handleRequiredCountChange = async (count: number | null) => {
    if (actionType !== 'CREATE') return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Update action with RequiredDocumentCount
      await apiService.updateAction(actionId, {
        requiredDocumentCount: count || undefined,
      });

      setRequiredDocumentCount(count);
      
      // Wait for backend to process placeholder creation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reload associations with retry logic (backend creates placeholders automatically)
      let docs: DocumentAssociation[] = [];
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries && (count === null || docs.length < count)) {
        console.log(`Reloading associations after count change (attempt ${retryCount + 1}/${maxRetries})...`);
        docs = await apiService.getActionDocuments(actionId);
        
        if (count !== null && docs.length < count && retryCount < maxRetries - 1) {
          console.log(`Only ${docs.length} documents found, expected ${count}. Waiting 1 second before retry...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        retryCount++;
      }
      
      setAssociations(docs);

      if (count !== null && docs.length >= count) {
        setSuccess(`Document count set to ${count}. ${docs.length} placeholder(s) created.`);
      } else if (count === null) {
        setSuccess('Document count cleared.');
      } else {
        setSuccess(`Document count set to ${count}. ${docs.length} placeholder(s) found (may still be creating).`);
      }
      
      if (onUpdateAssociations) {
        onUpdateAssociations(docs);
      }
    } catch (err: unknown) {
      console.error('Failed to set required document count:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to set required document count';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMultiDocumentSelect = async (
    selectedDocs: AvailableCreateDocument[],
    documentType: 'SOURCE' | 'REFERENCE'
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const newAssociations = selectedDocs.map(doc => ({
        documentType,
        sourceActionId: doc.actionId,
        sourceActionDocumentId: doc.actionDocumentId,
        documentNumber: doc.documentNumber,
        isRequired: documentType === 'SOURCE',
      }));

      console.log(`Creating ${actionType} associations:`, newAssociations);

      const created = await apiService.createActionDocuments(actionId, newAssociations);
      setAssociations(created);
      setSelectedDocuments(selectedDocs.map(d => d.documentNumber));
      setSelectedDocumentActionIds(selectedDocs.map(d => d.actionDocumentId));
      setSuccess(`Associated ${selectedDocs.length} document(s) with this action.`);

      if (onCreateAssociations) {
        onCreateAssociations(created);
      }
    } catch (err: unknown) {
      console.error('Failed to create document associations:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create document associations';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReviseDocumentSelect = async (selectedDocs: AvailableCreateDocument[]) => {
    await handleMultiDocumentSelect(selectedDocs, 'SOURCE');
  };

  // Handle document selection for REVIEW/APPROVE actions
  const handleReviewApproveDocumentSelect = async (selectedDoc: AvailableCreateDocument | null) => {
    if (selectedDoc === null) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create association with source action ID and specific document ID
      const newAssociations = [{
        documentType: 'SOURCE',
        sourceActionId: selectedDoc.actionId, // Source CREATE action ID
        sourceActionDocumentId: selectedDoc.actionDocumentId, // Specific document ID from source action
        documentNumber: selectedDoc.documentNumber,
        isRequired: true,
      }];

      console.log('Creating REVIEW/APPROVE association with source document ID:', newAssociations);

      const created = await apiService.createActionDocuments(actionId, newAssociations);
      setAssociations(created);
      setSelectedDocument(selectedDoc.documentNumber);
      setSelectedDocumentActionId(selectedDoc.actionDocumentId);
      setSuccess('Document associated with this action.');

      if (onCreateAssociations) {
        onCreateAssociations(created);
      }
    } catch (err: unknown) {
      console.error('Failed to create document association:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create document association';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle title change
  const handleTitleChange = (actionDocumentId: number, title: string) => {
    setAssociations(prevDocs => {
      const updated = prevDocs.map(doc =>
        doc.actionDocumentId === actionDocumentId
          ? { ...doc, documentTitle: title }
          : doc
      );
      // Notify parent of changes so project save can include them
      if (onUpdateAssociations) {
        onUpdateAssociations(updated);
      }
      return updated;
    });
  };

  // Handle description change
  const handleDescriptionChange = (actionDocumentId: number, description: string) => {
    setAssociations(prevDocs => {
      const updated = prevDocs.map(doc =>
        doc.actionDocumentId === actionDocumentId
          ? { ...doc, documentDescription: description }
          : doc
      );
      // Notify parent of changes so project save can include them
      if (onUpdateAssociations) {
        onUpdateAssociations(updated);
      }
      return updated;
    });
  };

  // Handle target folder path change
  const handleFolderPathChange = (actionDocumentId: number, folderPath: string) => {
    setAssociations(prevDocs => {
      const updated = prevDocs.map(doc =>
        doc.actionDocumentId === actionDocumentId
          ? { ...doc, targetFolderPath: folderPath }
          : doc
      );
      // Notify parent of changes so project save can include them
      if (onUpdateAssociations) {
        onUpdateAssociations(updated);
      }
      return updated;
    });
  };

  // Validate titles (required for all CREATE actions)
  const validateTitles = (): boolean => {
    if (actionType === 'CREATE' && requiredDocumentCount && requiredDocumentCount > 0) {
      return associations.every(
        doc => !doc.isRequired || (doc.documentTitle && doc.documentTitle.trim().length > 0)
      );
    }
    return true;
  };

  // Save titles and descriptions
  const handleSaveTitles = async () => {
    if (!validateTitles()) {
      setError('Document titles are required for CREATE actions');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use the EXACT same lenient filter as the display logic to identify placeholders
      let placeholderDocs = associations
        .filter(doc => {
          // Very lenient: if it has no file (spItemId and localFileId both null/undefined), treat as placeholder
          const hasNoFile = !doc.spItemId && !doc.localFileId;
          
          // Also check explicit isPlaceholder flag
          const explicitlyPlaceholder = doc.isPlaceholder === true;
          
          // Include if: explicitly marked as placeholder OR has no file reference
          // Don't exclude based on isRequired - be lenient
          return hasNoFile || explicitlyPlaceholder;
        })
        .sort((a, b) => (a.documentNumber || 0) - (b.documentNumber || 0));
      
      // Fallback: If we have exactly requiredDocumentCount associations with no files, show them all
      // This handles cases where isPlaceholder flag might not be set correctly
      if (placeholderDocs.length === 0 && requiredDocumentCount && associations.length === requiredDocumentCount) {
        const allHaveNoFiles = associations.every(doc => !doc.spItemId && !doc.localFileId);
        if (allHaveNoFiles) {
          console.log('Save: Fallback - Using all associations as placeholders (all have no files and count matches)');
          placeholderDocs = [...associations].sort((a, b) => (a.documentNumber || 0) - (b.documentNumber || 0));
        }
      }

      const updates = placeholderDocs.map(doc => ({
        actionDocumentId: doc.actionDocumentId,
        documentTitle: doc.documentTitle?.trim() || null,
        documentDescription: doc.documentDescription?.trim() || null,
        targetFolderPath: doc.targetFolderPath?.trim() || null
      }));

      console.log('Saving document titles/descriptions/folders:', {
        actionId,
        placeholderDocsCount: placeholderDocs.length,
        totalAssociations: associations.length,
        requiredDocumentCount,
        updates: updates.map(u => ({
          actionDocumentId: u.actionDocumentId,
          documentTitle: u.documentTitle,
          documentDescription: u.documentDescription,
          targetFolderPath: u.targetFolderPath
        }))
      });

      if (updates.length === 0) {
        console.warn('No placeholder documents found to save', {
          associationsCount: associations.length,
          associations: associations.map(doc => ({
            actionDocumentId: doc.actionDocumentId,
            documentNumber: doc.documentNumber,
            isPlaceholder: doc.isPlaceholder,
            spItemId: doc.spItemId,
            localFileId: doc.localFileId,
            hasFile: !!(doc.spItemId || doc.localFileId)
          }))
        });
        setLoading(false);
        return;
      }

      const result = await apiService.updateActionDocuments(actionId, updates);
      
      console.log('Save response from backend:', {
        resultLength: result.length,
        result: result.map(r => ({
          actionDocumentId: r.actionDocumentId,
          documentTitle: r.documentTitle,
          documentDescription: r.documentDescription
        }))
      });
      
      // Update local state with saved titles from backend response
      setAssociations(prevDocs =>
        prevDocs.map(doc => {
          const updated = result.find(r => r.actionDocumentId === doc.actionDocumentId);
          if (updated) {
            return { 
              ...doc, 
              documentTitle: updated.documentTitle || doc.documentTitle, 
              documentDescription: updated.documentDescription || doc.documentDescription 
            };
          }
          return doc;
        })
      );
      
      // Reload associations from backend to ensure we have the latest data
      const reloadedDocs = await apiService.getActionDocuments(actionId);
      console.log('Reloaded associations after save:', {
        reloadedCount: reloadedDocs.length,
        reloadedDocs: reloadedDocs.map(doc => ({
          actionDocumentId: doc.actionDocumentId,
          documentNumber: doc.documentNumber,
          documentTitle: doc.documentTitle,
          documentDescription: doc.documentDescription
        }))
      });
      setAssociations(reloadedDocs);
      
      if (onUpdateAssociations) {
        onUpdateAssociations(reloadedDocs);
      }

      setSuccess('Document details saved successfully.');
    } catch (err: unknown) {
      console.error('Failed to save document titles:', err);
      if (err && typeof err === 'object' && 'code' in err) {
        const errorCode = (err as { code?: string }).code;
        if (errorCode === 'TITLE_REQUIRED') {
          setError('Document title is required for CREATE actions');
        } else if (errorCode === 'TITLE_TOO_LONG') {
          setError(`Document title must not exceed 100 characters`);
        } else if (errorCode === 'DESCRIPTION_TOO_LONG') {
          setError(`Document description must not exceed 500 characters`);
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Failed to save document titles and descriptions';
          setError(errorMessage);
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save document titles and descriptions';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get document display name (title or fallback to "Document {number}")
  const getDocumentDisplayName = (doc: DocumentAssociation): string => {
    return doc.documentTitle || `Document ${doc.documentNumber}`;
  };

  // Handle delete association
  const handleDeleteAssociation = async (documentId: number) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiService.deleteActionDocument(actionId, documentId);
      const updated = associations.filter(a => a.actionDocumentId !== documentId);
      setAssociations(updated);
      setSuccess('Document association removed.');

      if (onUpdateAssociations) {
        onUpdateAssociations(updated);
      }
    } catch (err: unknown) {
      console.error('Failed to delete document association:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete document association';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Render CREATE action UI
  const renderCreateAction = () => {
    const uploadedCount = associations.filter(a => !a.isPlaceholder).length;
    const placeholderCount = associations.filter(a => a.isPlaceholder).length;

    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Required Document Count"
            type="number"
            value={requiredDocumentCount || ''}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              handleRequiredCountChange(isNaN(value) ? null : Math.max(1, value));
            }}
            inputProps={{ min: 1 }}
            disabled={loading}
            fullWidth
            helperText="Number of documents required for this action"
          />
        </Box>

        {requiredDocumentCount !== null && requiredDocumentCount > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Document Progress
            </Typography>
            <Typography variant="h6">
              {uploadedCount} of {requiredDocumentCount} documents uploaded
            </Typography>
            {placeholderCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {placeholderCount} placeholder(s) waiting for upload
              </Typography>
            )}
          </Box>
        )}

        {/* Document Titles & Descriptions Section for CREATE actions */}
        {actionType === 'CREATE' && requiredDocumentCount && requiredDocumentCount > 0 && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Document Titles & Descriptions
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Titles are required for CREATE actions
            </Typography>
            
            {(() => {
              // Filter for placeholder documents - be very lenient
              // If we have requiredDocumentCount > 1, show ALL associations that don't have files attached yet
              let placeholderDocs = associations
                .filter(doc => {
                  // Very lenient: if it has no file (spItemId and localFileId both null/undefined), treat as placeholder
                  const hasNoFile = !doc.spItemId && !doc.localFileId;
                  
                  // Also check explicit isPlaceholder flag
                  const explicitlyPlaceholder = doc.isPlaceholder === true;
                  
                  // Include if: explicitly marked as placeholder OR has no file reference
                  // Don't exclude based on isRequired - be lenient
                  return hasNoFile || explicitlyPlaceholder;
                })
                .sort((a, b) => (a.documentNumber || 0) - (b.documentNumber || 0));
              
              // Fallback: If we have exactly requiredDocumentCount associations with no files, show them all
              // This handles cases where isPlaceholder flag might not be set correctly
              if (placeholderDocs.length === 0 && requiredDocumentCount && associations.length === requiredDocumentCount) {
                const allHaveNoFiles = associations.every(doc => !doc.spItemId && !doc.localFileId);
                if (allHaveNoFiles) {
                  console.log('Fallback: Using all associations as placeholders (all have no files and count matches)');
                  placeholderDocs = [...associations].sort((a, b) => (a.documentNumber || 0) - (b.documentNumber || 0));
                }
              }
              
              console.log('Document Titles Section Debug:', {
                actionId,
                requiredDocumentCount,
                totalAssociations: associations.length,
                placeholderDocsCount: placeholderDocs.length,
                associations: associations.map(doc => ({
                  actionDocumentId: doc.actionDocumentId,
                  actionId: doc.actionId,
                  documentNumber: doc.documentNumber,
                  isPlaceholder: doc.isPlaceholder,
                  isRequired: doc.isRequired,
                  spItemId: doc.spItemId,
                  localFileId: doc.localFileId,
                  hasFile: !!(doc.spItemId || doc.localFileId),
                  documentTitle: doc.documentTitle,
                  documentDescription: doc.documentDescription
                })),
                filteredPlaceholders: placeholderDocs.map(doc => ({
                  actionDocumentId: doc.actionDocumentId,
                  documentNumber: doc.documentNumber,
                  isPlaceholder: doc.isPlaceholder,
                  hasFile: !!(doc.spItemId || doc.localFileId)
                }))
              });
              
              // If no placeholders found but we have a required count, show a message with reload button
              // Placeholders should have been auto-created on load, so this is likely a timing issue
              if (placeholderDocs.length === 0 && requiredDocumentCount > 0) {
                console.warn('No placeholder docs found after filtering, but requiredDocumentCount > 0', {
                  requiredDocumentCount,
                  totalAssociations: associations.length,
                  associations: associations
                });
                return (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    action={
                      <Button 
                        size="small" 
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const docs = await apiService.getActionDocuments(actionId);
                            console.log('Reloaded associations:', {
                              associationsCount: docs.length,
                              associations: docs.map(doc => ({
                                actionDocumentId: doc.actionDocumentId,
                                documentNumber: doc.documentNumber,
                                documentTitle: doc.documentTitle,
                                documentDescription: doc.documentDescription,
                                isPlaceholder: doc.isPlaceholder
                              }))
                            });
                            setAssociations(docs);
                            const action = await apiService.getAction(actionId);
                            if (action.requiredDocumentCount) {
                              setRequiredDocumentCount(action.requiredDocumentCount);
                            }
                          } catch (err: unknown) {
                            console.error('Failed to reload associations:', err);
                            const errorMessage = err instanceof Error ? err.message : 'Failed to reload associations';
                            setError(errorMessage);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                      >
                        Reload
                      </Button>
                    }
                  >
                    Placeholders should be created automatically. If you don't see them, click "Reload" to refresh.
                  </Alert>
                );
              }
              
              return placeholderDocs.map((doc) => (
                <Box key={doc.actionDocumentId} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Document {doc.documentNumber}
                  </Typography>
                  
                  <TextField
                    label="Document Title"
                    value={doc.documentTitle || ''}
                    onChange={(e) => handleTitleChange(doc.actionDocumentId, e.target.value)}
                    required
                    error={!doc.documentTitle?.trim() && doc.isRequired}
                    helperText={
                      !doc.documentTitle?.trim() && doc.isRequired 
                        ? "Title is required for CREATE actions" 
                        : `${doc.documentTitle?.length || 0}/100 characters`
                    }
                    inputProps={{ maxLength: 100 }}
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={loading}
                  />
                  
                  <TextField
                    label="Description (Optional)"
                    value={doc.documentDescription || ''}
                    onChange={(e) => handleDescriptionChange(doc.actionDocumentId, e.target.value)}
                    multiline
                    rows={2}
                    helperText={`${doc.documentDescription?.length || 0}/500 characters`}
                    inputProps={{ maxLength: 500 }}
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={loading}
                  />

                  {/* Target Folder Selection */}
                  <SharePointFolderPicker
                    projectId={projectId}
                    selectedPath={doc.targetFolderPath || null}
                    onPathChange={(path) => handleFolderPathChange(doc.actionDocumentId, path)}
                    disabled={loading}
                    label="Target Folder"
                    helperText="Select or create a folder for this document within the project's SharePoint folder"
                  />
                </Box>
              ));
            })()}
            
            {(() => {
              // Use the EXACT same filter logic as the input fields above
              let placeholderDocs = associations
                .filter(doc => {
                  // Very lenient: if it has no file (spItemId and localFileId both null/undefined), treat as placeholder
                  const hasNoFile = !doc.spItemId && !doc.localFileId;
                  
                  // Also check explicit isPlaceholder flag
                  const explicitlyPlaceholder = doc.isPlaceholder === true;
                  
                  // Include if: explicitly marked as placeholder OR has no file reference
                  // Don't exclude based on isRequired - be lenient
                  return hasNoFile || explicitlyPlaceholder;
                })
                .sort((a, b) => (a.documentNumber || 0) - (b.documentNumber || 0));
              
              // Fallback: If we have exactly requiredDocumentCount associations with no files, show them all
              if (placeholderDocs.length === 0 && requiredDocumentCount && associations.length === requiredDocumentCount) {
                const allHaveNoFiles = associations.every(doc => !doc.spItemId && !doc.localFileId);
                if (allHaveNoFiles) {
                  placeholderDocs = [...associations].sort((a, b) => (a.documentNumber || 0) - (b.documentNumber || 0));
                }
              }
              
              if (placeholderDocs.length > 0) {
                return (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveTitles}
                    disabled={loading || !validateTitles()}
                    sx={{ mt: 2 }}
                    fullWidth
                  >
                    Save Titles & Descriptions
                  </Button>
                );
              }
              return null;
            })()}
          </Box>
        )}

        {associations.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Document Associations
            </Typography>
            <List dense>
              {associations.map((doc) => (
                <ListItem
                  key={doc.actionDocumentId}
                  secondaryAction={
                    !doc.isPlaceholder && (
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteAssociation(doc.actionDocumentId)}
                        disabled={loading}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )
                  }
                >
                  <ListItemIcon>
                    {doc.isPlaceholder ? (
                      <RadioButtonUnchecked color="disabled" />
                    ) : (
                      <CheckCircle color="success" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      doc.isPlaceholder
                        ? `${getDocumentDisplayName(doc)} (Placeholder)`
                        : doc.fileName || getDocumentDisplayName(doc)
                    }
                    secondary={
                      <>
                        {doc.documentDescription && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {doc.documentDescription}
                          </Typography>
                        )}
                        {doc.targetFolderPath && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Folder sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {doc.targetFolderPath === '/' ? 'Project Root' : doc.targetFolderPath}
                            </Typography>
                          </Box>
                        )}
                        {!doc.isPlaceholder && (
                          <>
                            {doc.storageType && (
                              <Chip
                                icon={doc.storageType === 'SHAREPOINT' ? <CloudUpload /> : <Storage />}
                                label={doc.storageType}
                                size="small"
                                sx={{ mr: 1 }}
                              />
                            )}
                            {doc.fileSize && ` • ${(doc.fileSize / 1024 / 1024).toFixed(2)} MB`}
                            {doc.uploadedAt && ` • Uploaded ${new Date(doc.uploadedAt).toLocaleDateString()}`}
                          </>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  // Render REVISE action UI
  const renderReviseAction = () => {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Select CREATE documents from prerequisite actions to revise:
        </Typography>

        <Autocomplete
          multiple
          options={availableCreateDocuments}
          getOptionLabel={(option) => option.label}
          value={availableCreateDocuments.filter(doc =>
            selectedDocumentActionIds.includes(doc.actionDocumentId)
          )}
          onChange={(_, newValue) => {
            const docNumbers = newValue.map(v => v.documentNumber);
            const actionDocIds = newValue.map(v => v.actionDocumentId);
            setSelectedDocuments(docNumbers);
            setSelectedDocumentActionIds(actionDocIds);
            handleReviseDocumentSelect(newValue); // Pass full document objects
          }}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Documents"
              placeholder="Choose CREATE documents to revise"
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} />
              <Box>
                <Typography variant="body2">
                  {option.documentTitle || `Document ${option.documentNumber}`}
                </Typography>
                {option.documentDescription && (
                  <Typography variant="caption" color="text.secondary">
                    {option.documentDescription}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  From: {option.label.split(' → ')[0]} (Document {option.documentNumber})
                </Typography>
              </Box>
            </li>
          )}
          sx={{ mb: 2 }}
        />

        {associations.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Associated Documents
            </Typography>
            <List dense>
              {associations.map((doc) => (
                <ListItem
                  key={doc.actionDocumentId}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteAssociation(doc.actionDocumentId)}
                      disabled={loading}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <Description />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.fileName || getDocumentDisplayName(doc)}
                    secondary={
                      <>
                        {doc.documentDescription && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {doc.documentDescription}
                          </Typography>
                        )}
                        {doc.storageType && (
                          <Chip
                            icon={doc.storageType === 'SHAREPOINT' ? <CloudUpload /> : <Storage />}
                            label={doc.storageType}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                        )}
                        Document Number: {doc.documentNumber}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  const renderMultiReferenceAction = (documentType: 'SOURCE' | 'REFERENCE') => {
    const isValidate = actionType === 'VALIDATE';
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {isValidate
            ? 'Optionally select CREATE documents that provide supporting information for this validation. They are informational only.'
            : 'Select one or more CREATE documents from prerequisite actions to review:'}
        </Typography>

        <Autocomplete
          multiple
          options={availableCreateDocuments}
          getOptionLabel={(option) => option.label}
          value={availableCreateDocuments.filter(doc =>
            selectedDocumentActionIds.includes(doc.actionDocumentId)
          )}
          onChange={(_, newValue) => {
            const docNumbers = newValue.map(v => v.documentNumber);
            const actionDocIds = newValue.map(v => v.actionDocumentId);
            setSelectedDocuments(docNumbers);
            setSelectedDocumentActionIds(actionDocIds);
            handleMultiDocumentSelect(newValue, documentType);
          }}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={isValidate ? 'Reference Documents (optional)' : 'Select Documents'}
              placeholder={isValidate ? 'Choose supporting documents' : 'Choose CREATE documents to review'}
              helperText={isValidate ? 'Zero or more allowed' : 'At least one document is required'}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} />
              <Box>
                <Typography variant="body2">
                  {option.documentTitle || `Document ${option.documentNumber}`}
                </Typography>
                {option.documentDescription && (
                  <Typography variant="caption" color="text.secondary">
                    {option.documentDescription}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  From: {option.label.split(' → ')[0]} (Document {option.documentNumber})
                </Typography>
              </Box>
            </li>
          )}
          sx={{ mb: 2 }}
        />

        {associations.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Associated {isValidate ? 'Reference ' : ''}Documents
            </Typography>
            <List dense>
              {associations.map((doc) => (
                <ListItem
                  key={doc.actionDocumentId}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteAssociation(doc.actionDocumentId)}
                      disabled={loading}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <Description />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.fileName || getDocumentDisplayName(doc)}
                    secondary={
                      <>
                        {doc.documentDescription && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {doc.documentDescription}
                          </Typography>
                        )}
                        {doc.storageType && (
                          <Chip
                            icon={doc.storageType === 'SHAREPOINT' ? <CloudUpload /> : <Storage />}
                            label={doc.storageType}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                        )}
                        Document Number: {doc.documentNumber}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {!isValidate && associations.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Review actions require at least one reference document.
          </Alert>
        )}
      </Box>
    );
  };

  // Render APPROVE action UI (single document)
  const renderReviewApproveAction = () => {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Select a CREATE document from prerequisite actions to {actionType.toLowerCase()}:
        </Typography>

        <Autocomplete
          options={availableCreateDocuments}
          getOptionLabel={(option) => option.label}
          value={
            availableCreateDocuments.find(doc => doc.actionDocumentId === selectedDocumentActionId) || null
          }
          onChange={(_, newValue) => {
            setSelectedDocument(newValue?.documentNumber || null);
            setSelectedDocumentActionId(newValue?.actionDocumentId || null);
            handleReviewApproveDocumentSelect(newValue || null); // Pass full document object
          }}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Document"
              placeholder="Choose a CREATE document"
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography variant="body2">
                  {option.documentTitle || `Document ${option.documentNumber}`}
                </Typography>
                {option.documentDescription && (
                  <Typography variant="caption" color="text.secondary">
                    {option.documentDescription}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  From: {option.label.split(' → ')[0]} (Document {option.documentNumber})
                </Typography>
              </Box>
            </li>
          )}
          sx={{ mb: 2 }}
        />

        {associations.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Associated Document
            </Typography>
            <List dense>
              {associations.map((doc) => (
                <ListItem
                  key={doc.actionDocumentId}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteAssociation(doc.actionDocumentId)}
                      disabled={loading}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <Description />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.fileName || getDocumentDisplayName(doc)}
                    secondary={
                      <>
                        {doc.documentDescription && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {doc.documentDescription}
                          </Typography>
                        )}
                        {doc.storageType && (
                          <Chip
                            icon={doc.storageType === 'SHAREPOINT' ? <CloudUpload /> : <Storage />}
                            label={doc.storageType}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                        )}
                        Document Number: {doc.documentNumber}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Document Associations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Action Type: <strong>{actionType}</strong>
      </Typography>

      <Divider sx={{ my: 2 }} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {actionType === 'CREATE' && renderCreateAction()}
      {actionType === 'REVISE' && renderReviseAction()}
      {actionType === 'REVIEW' && renderMultiReferenceAction('SOURCE')}
      {actionType === 'VALIDATE' && renderMultiReferenceAction('REFERENCE')}
      {actionType === 'APPROVE' && renderReviewApproveAction()}
    </Paper>
  );
}
